import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Monitor, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const LogoutConfirmModal = ({ isDark, open, onCancel, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [confirmingScope, setConfirmingScope] = useState("");
  const [devices, setDevices] = useState([]);
  const [currentIp, setCurrentIp] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);
    setDevices([]);
    setCurrentIp("");
    setConfirmingScope("");

    axios
      .get(`${API}/auth/remember`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setDevices(Array.isArray(res.data?.devices) ? res.data.devices : []);
        setCurrentIp(res.data?.current_ip || "");
      })
      .catch(() => {
        if (!cancelled) {
          setDevices([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, onCancel]);

  if (!open) return null;

  const theme = isDark ? "dark" : "light";
  const overlayClass = `login-otp-overlay fixed inset-0 z-[1200] flex items-center justify-center p-4 backdrop-blur-md ${isDark ? "bg-slate-950/70" : "bg-slate-900/35"}`;
  const cardClass = `login-otp-card login-otp-card--${theme} w-full max-w-lg`;
  const headerClass = `login-otp-header login-otp-header--${theme}`;
  const glowClass = `login-otp-header-glow login-otp-header-glow--${theme}`;
  const iconWrapClass = `login-otp-icon-wrap login-otp-icon-wrap--danger login-otp-icon-wrap--${theme}`;
  const closeClass = `login-otp-close login-otp-close--${theme}`;
  const mutedClass = isDark ? "text-slate-400" : "text-slate-500";
  const titleClass = isDark ? "text-white" : "text-slate-900";
  const hasRemember = devices.length > 0;
  const hasOtherDevices = devices.some((device) => !device.is_current);
  const hasCurrentDevice = devices.some((device) => device.is_current);
  const isConfirming = confirmingScope !== "";

  const handleLogout = async (scope) => {
    setConfirmingScope(scope);
    try {
      await onLogout(scope);
    } finally {
      setConfirmingScope("");
    }
  };

  return (
    <div className={overlayClass} role="dialog" aria-modal="true" aria-labelledby="logout-confirm-title">
      <div className={cardClass}>
        <div className={headerClass}>
          <div className={glowClass} aria-hidden="true" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={iconWrapClass}>
                <LogOut className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 id="logout-confirm-title" className={`text-lg sm:text-xl font-semibold tracking-tight ${titleClass}`}>
                  Çıkış yap
                </h2>
                <p className={`text-sm mt-1 leading-relaxed ${mutedClass}`}>
                  {hasOtherDevices
                    ? "Başka cihazlarda hatırlanan oturumlar açık kalabilir. Nasıl çıkmak istediğinizi seçin."
                    : "Oturumunuz sonlandırılacak. Devam etmek istiyor musunuz?"}
                </p>
              </div>
            </div>
            <button type="button" onClick={onCancel} className={closeClass} aria-label="Kapat">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="login-otp-body">
          {loading ? (
            <div className={`login-otp-logout-loading ${mutedClass}`}>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Hatırlanan cihazlar kontrol ediliyor…</span>
            </div>
          ) : hasRemember ? (
            <>
              <div className={`login-otp-remember-note login-otp-remember-note--${theme}`}>
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
                <p>
                  <strong>Beni hatırla</strong> aktif. Aşağıdaki IP adreslerinden seçilen süre boyunca OTP olmadan otomatik giriş yapılabilir.
                </p>
              </div>

              <ul className="login-otp-logout-device-list">
                {devices.map((device) => (
                  <li
                    key={device.id}
                    className={`login-otp-logout-device login-otp-logout-device--${theme}${device.is_current ? " login-otp-logout-device--current" : ""}`}
                  >
                    <div className="login-otp-logout-device-main">
                      <Monitor className="w-4 h-4 shrink-0 opacity-75" />
                      <div className="min-w-0">
                        <p className="login-otp-logout-device-ip">{device.ip_address}</p>
                        <p className={`login-otp-logout-device-label ${mutedClass}`}>
                          {device.device_label || "Bilinmeyen cihaz"}
                        </p>
                      </div>
                    </div>
                    <div className="login-otp-logout-device-meta">
                      {device.is_current && (
                        <span className="login-otp-logout-device-badge login-otp-logout-device-badge--current">
                          Bu cihaz
                        </span>
                      )}
                      <span className={`login-otp-logout-device-days ${mutedClass}`}>
                        {device.days_left} gün kaldı
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              {currentIp && (
                <p className={`text-[11px] mt-2 ${mutedClass}`}>
                  Şu anki IP: <span className="font-mono">{currentIp}</span>
                </p>
              )}
            </>
          ) : (
            <div className={`login-otp-remember-note login-otp-remember-note--${theme}`}>
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
              <p>Bu hesap için aktif <strong>beni hatırla</strong> kaydı bulunmuyor.</p>
            </div>
          )}

          <div className="flex flex-col gap-2.5 mt-4">
            {hasOtherDevices ? (
              <>
                <Button
                  type="button"
                  disabled={isConfirming || loading}
                  onClick={() => handleLogout("session")}
                  className="login-otp-primary-btn"
                >
                  {confirmingScope === "session" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Şu anki oturumdan çık
                </Button>
                {hasCurrentDevice && (
                  <Button
                    type="button"
                    disabled={isConfirming || loading}
                    onClick={() => handleLogout("device")}
                    className="login-otp-primary-btn login-otp-primary-btn--danger"
                  >
                    {confirmingScope === "device" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Bu cihazı unut ve çık
                  </Button>
                )}
                <Button
                  type="button"
                  disabled={isConfirming || loading}
                  onClick={() => handleLogout("all")}
                  variant="outline"
                  className={`h-10 rounded-xl border ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-950/40" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                >
                  {confirmingScope === "all" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Tüm hatırlanan cihazlardan çık
                </Button>
              </>
            ) : (
              <Button
                type="button"
                disabled={isConfirming || loading}
                onClick={() => handleLogout(hasRemember && hasCurrentDevice ? "device" : "session")}
                className="login-otp-primary-btn login-otp-primary-btn--danger"
              >
                {isConfirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Çıkış yap
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              disabled={isConfirming}
              onClick={onCancel}
              className={`h-10 rounded-xl ${mutedClass} ${isDark ? "hover:bg-slate-800/80 hover:text-slate-200" : "hover:bg-slate-100 hover:text-slate-800"}`}
            >
              Vazgeç
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmModal;
